import { useApi } from '@backstage/core-plugin-api';
import { uptimeMonitorApiRef } from '../api/UptimeMonitorApi';
import { useState, useEffect, useCallback } from 'react';
import { AIPredictionResponse, GetAIPredictionRequest } from '../types';

export const useAISuggestion = (eventId: string | null) => {
  const api = useApi(uptimeMonitorApiRef);
  const [suggestion, setSuggestion] = useState<AIPredictionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const fetchSuggestion = useCallback(async (currentEventId: string) => {
    setIsLoading(true);
    setError(undefined);
    
    try {
      const params: GetAIPredictionRequest = {
        data_id: currentEventId,
      };
      
      const result = await api.getAIPrediction(params);
      
      // Only update state if this is still the current eventId
      setSuggestion(result);
    } catch (err: any) {
      console.error('Error fetching AI suggestion:', err);
      
      // If it's a 404 or prediction doesn't exist, set suggestion to null
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        setSuggestion(null);
      } else {
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  const refetch = useCallback(() => {
    if (eventId) {
      fetchSuggestion(eventId);
    }
  }, [eventId, fetchSuggestion]);

  useEffect(() => {
    // Reset state immediately when eventId changes
    setSuggestion(null);
    setError(undefined);
    
    if (eventId) {
      fetchSuggestion(eventId);
    } else {
      setIsLoading(false);
    }
  }, [eventId, fetchSuggestion]);

  return {
    isLoading,
    suggestion,
    error,
    refetch,
    hasSuggestion: suggestion !== null && suggestion !== undefined,
  };
};